import { Alert, Button, Typography } from "antd";
import type { PurchaseInvoice, PurchaseReconciliation, Project } from "@zhihaoscm/entity";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Grid as ZhGrid,
    GridItem as ZhGridItem,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import request from "@/api";
import { DATE_PICKER_FORMAT, DATETIME_PICKER_FORMAT, DEFAULT_VALUE } from "@/config";
import { INVOICE_TYPE } from "@/meta/purchase/invoice";
import { S_INVOICE_STATE } from "@zhihaoscm/meta";
import { dateFormat } from "@/utils";
import { formatNumber } from "@/utils/number";

type InvoiceEnterprise = Partial<PurchaseInvoice["applyEnterprise"]>;

export interface InvoiceManagementDetailData {
    purchaseInvoice?: Partial<PurchaseInvoice> & { state?: number | string };
    project?: Partial<Project>;
    purchaseReconciliations?: Array<Partial<PurchaseReconciliation>>;
}

export interface InvoiceManagementDetailPageProps {
    invoiceId?: number | string;
    loadInvoiceDetail?: (id: number | string) => Promise<InvoiceManagementDetailData>;
}

/** 复用 Vue 发票详情的 `/admin/purchase-invoice/vo/:id` 接口。 */
export async function loadInvoiceManagementDetail(id: number | string): Promise<InvoiceManagementDetailData> {
    const { data } = await request.service.get<InvoiceManagementDetailData>(`/admin/purchase-invoice/vo/${id}`);
    return data;
}

/** 详情字段统一使用 Vue 组件的默认占位符。 */
function display(value: unknown): string {
    return value === undefined || value === null || value === "" ? DEFAULT_VALUE : String(value);
}

/** 保持企业名称回退顺序与 Vue `getEnterpriseName` 一致。 */
function formatEnterpriseName(enterprise?: InvoiceEnterprise): string {
    return enterprise?.name || enterprise?.realName || enterprise?.mobile || DEFAULT_VALUE;
}

/** 对齐 Vue 的日期格式化规则，详情头部显示到分钟。 */
function formatDetailDate(value?: string): string {
    return value ? dateFormat(value, DATETIME_PICKER_FORMAT) : DEFAULT_VALUE;
}

/** 对账单详情页：迁移 Vue 字段、跳转、返回和加载失败反馈。 */
export function InvoiceManagementDetailPage({
    invoiceId,
    loadInvoiceDetail = loadInvoiceManagementDetail
}: InvoiceManagementDetailPageProps) {
    const params = useParams<{ id?: string }>();
    const navigate = useNavigate();
    const currentId = invoiceId ?? params.id ?? "";
    const [detail, setDetail] = useState<InvoiceManagementDetailData>();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (!currentId) return;
        let disposed = false;
        void loadInvoiceDetail(currentId)
            .then(value => {
                if (disposed) return;
                setDetail(value);
                setErrorMessage(null);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("发票详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [currentId, loadInvoiceDetail]);

    const invoice = detail?.purchaseInvoice;
    const project = detail?.project;
    const reconciliations = detail?.purchaseReconciliations || [];

    // 用 zhihao-ui-react 列契约承接 Vue 发票详情对账单的链接、日期和金额格式化。
    const reconciliationColumns: TableColumnProps<Partial<PurchaseReconciliation>>[] = [
        {
            key: "id",
            label: "对账单",
            minWidth: 100,
            prop: "id",
            render: ({ row }) => <Link to={`/purchasingManage/reconciliation/info/${row.id}`}>{display(row.id)}</Link>
        },
        {
            key: "reconciliationDate",
            label: "日期",
            minWidth: 100,
            prop: "reconciliationDate",
            render: ({ row }) =>
                row.reconciliationDate ? dateFormat(row.reconciliationDate, DATE_PICKER_FORMAT) : DEFAULT_VALUE
        },
        {
            key: "quantity",
            label: "吨数",
            minWidth: 100,
            prop: "quantity",
            render: ({ row }) => formatNumber(row.quantity)
        },
        {
            key: "unitPrice",
            label: "单价(元/吨)",
            minWidth: 100,
            // 保持 Vue ProTable 的列字段契约，单价由实际金额和吨数格式化得到。
            prop: "unitAmount",
            render: ({ row }) =>
                row.quantity && row.actualAmount != null
                    ? formatNumber(row.actualAmount / row.quantity, 2)
                    : DEFAULT_VALUE
        },
        {
            key: "actualAmount",
            label: "折扣金额(元)",
            minWidth: 100,
            prop: "actualAmount",
            render: ({ row }) => formatNumber(row.actualAmount, 2)
        }
    ];

    return (
        <section aria-label="invoice-management-detail-page" className="flex min-h-full flex-col">
            <div className="section-card">
                <ZhDetailHeader
                    leftInfos={[{ label: "编号", value: display(invoice?.id) }]}
                    rightInfos={[{ label: "创建时间", value: formatDetailDate(invoice?.createdTime) }]}
                    stateDesc={S_INVOICE_STATE.getIntroByCode(invoice?.state) || DEFAULT_VALUE}
                    title="开票详情"
                />
                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}

                <Typography.Title level={5}>发票信息</Typography.Title>
                <ZhGrid cols={4} gap={[20, 20]}>
                    <ZhBaseItem label="所属项目">
                        {invoice?.projectId ? (
                            <Link to={`/purchasingManage/project/info/${invoice.projectId}`}>
                                {display(invoice.projectName)}
                            </Link>
                        ) : (
                            display(invoice?.projectName)
                        )}
                    </ZhBaseItem>
                    <ZhBaseItem label="类型" value={INVOICE_TYPE.getIntroByCode(invoice?.type) || DEFAULT_VALUE} />
                    <ZhBaseItem label="品类" value={display(project?.productTypeName)} />
                    <ZhGridItem />
                    <ZhBaseItem label="申请单位" value={formatEnterpriseName(invoice?.applyEnterprise)} />
                    <ZhBaseItem label="开票单位" value={display(invoice?.invoiceEnterprise?.name)} />
                    <ZhBaseItem label="凭证文件" span={4} value={display(invoice?.fileId)} />
                </ZhGrid>

                <Typography.Title className="mt-6" level={5}>
                    对账单
                </Typography.Title>
                <ZhTable<Partial<PurchaseReconciliation>>
                    data={reconciliations}
                    columns={reconciliationColumns}
                    emptyText="暂无对账单"
                    pagination={false}
                    rowKey={row => String(row.id)}
                />

                <Button className="mt-6" onClick={() => void navigate("/purchasingManage/invoiceManagement")}>
                    返回
                </Button>
            </div>
        </section>
    );
}

export default InvoiceManagementDetailPage;
